import {SkeletonText} from '@github-ui/code-view-shared/components/Skeleton'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {useRefInfo} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import {useReposAnalytics} from '@github-ui/code-view-shared/hooks/use-repos-analytics'
import {useCurrentRepository} from '@github-ui/current-repository'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {commitsPathByAuthor} from '@github-ui/paths'
import {Link} from '@github-ui/react-core/link'
import type {ContributorUser} from '@github-ui/repos-types'
import {AlertFillIcon, PeopleIcon} from '@primer/octicons-react'
import {ActionList, AvatarStack, CounterLabel, Link as PrimerLink, Truncate} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {useState} from 'react'

import {useContributorsContext} from '../contexts/ContributorsContext'
import styles from './Contributors.module.css'

export function ContributorAvatars({showTitle = true}: {showTitle?: boolean}) {
  const {contributors, loading, error} = useContributorsContext()
  const {sendRepoClickEvent} = useReposAnalytics()
  const [isDialogOpen, setDialogOpen] = useState(false)
  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  const maxAvatarCount = 10
  if (error) {
    return <ContributorsError />
  }

  if (loading) {
    return <SkeletonText width={100} data-testid="contributors-skeleton" />
  }

  if (!contributors || !contributors?.users.length) {
    return null
  }

  const {users, totalCount} = contributors
  const countTitle = getCountWithUnit(totalCount, ' contributor', 'contributors')

  return (
    <div className={styles.contributorsRow}>
      <div className={styles.avatarStackWrapper}>
        <AvatarStack alignRight>
          {users.slice(0, maxAvatarCount).map((user, index) => (
            <GitHubAvatar
              className={index > 5 ? 'AvatarShowLarge' : 'AvatarShowMedium'}
              key={user.login}
              src={user.primaryAvatarUrl}
              alt={user.login}
              data-testid="contributor-avatar"
              {...getHovercardAttributesForType('user', {login: user.login})}
            />
          ))}
        </AvatarStack>
      </div>

      <PrimerLink
        as="button"
        aria-label={`Show ${countTitle}"`}
        onClick={() => {
          setDialogOpen(true)
          sendRepoClickEvent('CONTRIBUTORS.LIST.OPEN')
        }}
        data-testid="contributors-count-button"
        className={styles.contributorsButton}
      >
        <PeopleIcon />
        {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
        {showTitle && <span className={clsx('react-contributors-title', styles.contributorsTitle)}>Contributors</span>}
        <CounterLabel className={styles.counterLabel}>{totalCount}</CounterLabel>
      </PrimerLink>

      {isDialogOpen && (
        <Dialog
          title={countTitle}
          onClose={() => setDialogOpen(false)}
          width="medium"
          height={contributors.totalCount >= 12 ? 'small' : 'auto'}
          renderBody={() => {
            return (
              <ActionList className={styles.contributorDialogList} data-testid="contributor-dialog-list">
                {users.map(user => (
                  <ContributorRow key={user.login} user={user} />
                ))}
              </ActionList>
            )
          }}
        />
      )}
    </div>
  )
}

function ContributorRow({user}: {user: ContributorUser}) {
  const {sendRepoClickEvent} = useReposAnalytics()
  const path = usePath()
  const refInfo = useRefInfo()
  const repo = useCurrentRepository()

  return (
    <ActionList.Item
      className={styles.contributorRow}
      data-testid="contributor-dialog-row"
      onSelect={() => sendRepoClickEvent('CONTRIBUTORS.LIST.USER')}
    >
      <PrimerLink
        as={Link}
        className={styles.contributorLink}
        muted
        to={user.profileLink}
        onClick={() => sendRepoClickEvent('CONTRIBUTORS.LIST.USER')}
      >
        <GitHubAvatar
          src={user.primaryAvatarUrl}
          alt={user.login}
          className={styles.contributorAvatar}
          aria-hidden="true"
        />
        <Truncate inline title={user.login}>
          {user.login}
        </Truncate>
      </PrimerLink>
      <ActionList.TrailingVisual>
        <PrimerLink
          as={Link}
          muted
          to={commitsPathByAuthor({repo, branch: refInfo.name, path, author: user.login})}
          onClick={() => sendRepoClickEvent('CONTRIBUTORS.LIST.COMMITS')}
          aria-label={`${getCountWithUnit(user.commitsCount, 'commit', 'commits')} by ${user.login}`}
          data-testid="commit-link"
        >
          {getCountWithUnit(user.commitsCount, 'commit', 'commits')}
        </PrimerLink>
      </ActionList.TrailingVisual>
    </ActionList.Item>
  )
}

function getCountWithUnit(count: number, singular: string, plural: string) {
  return `${count} ${count === 1 ? singular : plural}`
}

function ContributorsError() {
  return (
    <span className={styles.errorText}>
      <AlertFillIcon />
      &nbsp;Cannot retrieve contributors info at this time.
    </span>
  )
}
